import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./DermaHomePage.css";

// ── Blue elite variant — same images used in LifestyleCard v2 ─────────────────
const BG_ELITE = "https://appcdn.goqii.com/storeimg/50360_1777963086.png";
const RING     = "#4A77C0";

// ── shimmer (matches lc-shimmer in LifestyleCard) ─────────────────────────────
const shimmerCSS = `@keyframes dh-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`;
const _s = document.createElement("style");
_s.innerText = shimmerCSS;
document.head.appendChild(_s);

const shimmer = {
  background: "linear-gradient(90deg,var(--shimmer-1,#e0e0e0) 0%,var(--shimmer-2,#f5f5f5) 50%,var(--shimmer-1,#e0e0e0) 100%)",
  backgroundSize: "200% 100%",
  animation: "dh-shimmer 1.5s infinite",
};

// ── Skeleton — mirrors LifestyleCard Skeleton ─────────────────────────────────
const Skeleton = () => (
  <div className="dh-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 12px" }}>
    <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ width: 72, height: 72, borderRadius: "50%", ...shimmer }} />
    </div>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ height: 15, width: "55%", borderRadius: 6, ...shimmer }} />
      <div style={{ height: 11, width: "75%", borderRadius: 5, ...shimmer }} />
      <div style={{ height: 11, width: "60%", borderRadius: 5, ...shimmer }} />
    </div>
    <div style={{ flexShrink: 0, width: 70, height: 90, borderRadius: 10, ...shimmer }} />
  </div>
);

// ── Shared avatar style ───────────────────────────────────────────────────────
const avatarStyle = {
  width: 72,
  height: 72,
  borderRadius: "50%",
  border: `3px solid ${RING}`,
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
  backgroundColor: "#fff",
  flexShrink: 0,
};

// ── Certificate icon ──────────────────────────────────────────────────────────
const CertIcon = () => (
  <svg
    width="15" height="15" viewBox="0 0 24 24" fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0, marginRight: 4, verticalAlign: "middle" }}
  >
    <path
      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      fill="var(--color-primary)"
    />
  </svg>
);

// ── DoctorCard ────────────────────────────────────────────────────────────────
const DoctorCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    lifestyleApi
      .fetchDermaDoctor()
      .then((res) => {
        const d = res?.data?.data;
        if (d?.consent === "Yes") setData(d);
      })
      .catch((err) => console.error("DoctorCard:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  return (
    <div className="dh-card">
      {/* inner row — same padding/gap as LifestyleCard v2 */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 12px", position: "relative", zIndex: 1 }}>

        {/* LEFT — circular avatar with ring */}
        <img
          src={data.imageUrl || "https://appcdn.goqii.com/storeimg/64522_1711436754.jpg"}
          alt={data.doctorName}
          style={avatarStyle}
        />

        {/* MIDDLE — doctor info */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
          {/* row 1 — name + ID */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{
              fontSize: "calc(10px + 1vw)",
              fontWeight: 700,
              color: "var(--text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: "18px",
            }}>
              {data.doctorName}
            </span>
            {data.doctorId && (
              <span style={{
                fontSize: "calc(7px + 0.5vw)",
                fontWeight: 500,
                color: "var(--text-muted)",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}>
                ID {data.doctorId}
              </span>
            )}
          </div>

          {/* separator */}
          <div style={{ height: 1, background: "var(--divider)", opacity: 0.5, margin: "3px 0 5px" }} />

          {/* row 2 — Areas of Expertise */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
            <CertIcon />
            <span style={{ fontSize: "calc(8px + 0.8vw)", fontWeight: 600, color: "var(--text-primary)" }}>
              {t("dermaHome.doctor.areasOfExpertise")}
            </span>
          </div>

          {/* row 3 — degree */}
          {data.degree && (
            <p className="dh-muted-label" style={{ fontSize: "calc(7px + 1vw)" }}>
              {data.degree}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── UserCard ──────────────────────────────────────────────────────────────────
const UserCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const goqiiuserid = getGoqiiUserId();
  const profileLink = `https://goqii.com/app?fsn=71&fssn=0&fai={"friendId":"${goqiiuserid}"}`;

  useEffect(() => {
    lifestyleApi
      .fetchDermaUser()
      .then((res) => {
        if (res?.data) setData(res.data);
      })
      .catch((err) => console.error("UserCard:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton />;
  if (!data) return null;

  const userImg = data.image && data.image !== "" ? data.image : null;

  return (
    <a href={profileLink} style={{ textDecoration: "none", display: "block", color: "inherit" }}>
      <div className="dh-card">

        {/* ── Blue elite background image — same as .lc-bg-img ── */}
        <div className="dh-bg-img" style={{ backgroundImage: `url(${BG_ELITE})` }} />

        {/* ── Content row — matches LifestyleCard v2 inner layout ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 12px", position: "relative", zIndex: 1 }}>

          {/* LEFT — circular avatar with ring */}
          {userImg ? (
            <img src={userImg} alt={data.userName} style={avatarStyle} />
          ) : (
            <div style={{ ...avatarStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                <path d="M12 12C14.76 12 17 9.76 17 7C17 4.24 14.76 2 12 2C9.24 2 7 4.24 7 7C7 9.76 9.24 12 12 12Z" fill={RING} />
                <path d="M12 14C7.58 14 4 17.58 4 22H20C20 17.58 16.42 14 12 14Z" fill={RING} />
              </svg>
            </div>
          )}

          {/* MIDDLE — user info, matches v2 text sizing */}
          <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
            {/* row 1 — name */}
            <div style={{
              fontSize: "calc(10px + 1vw)",
              fontWeight: 700,
              color: "var(--text-primary)",
              lineHeight: "18px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: 4,
            }}>
              {data.userName || ""}
            </div>

            {/* row 2 — age (matches .lc-age-label style) */}
            {data.age && (
              <div className="dh-muted-label" style={{ fontSize: "calc(7px + 1vw)", marginBottom: 5 }}>
                {t("dermaHome.user.age")} — {data.age} {t("dermaHome.user.yrs")}
              </div>
            )}

            {/* row 3 — BSA (matches .lc-health-text style) */}
            {data.score !== undefined && data.score !== null && (
              <p className="dh-health-text" style={{ color: "var(--text-secondary)" }}>
                {t("dermaHome.user.bsaPrefix")} {data.score}%
              </p>
            )}

            {/* row 4 — tagline in green (matches health text pattern) */}
            <p className="dh-health-text" style={{ color: "var(--color-primary)", marginTop: 2 }}>
              {t("dermaHome.user.tagline")}
            </p>
          </div>

        </div>
      </div>
    </a>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const DermaHomePage = () => {
  const { theme, lang } = getUrlParams();

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  return (
    <div className="dh-page">
      <DoctorCard />
      <UserCard />
    </div>
  );
};

export default DermaHomePage;
